import { Alert, Badge, Button, EmptyState, Skeleton } from '@recipes/ui';
import { useLiveQuery } from 'dexie-react-hooks';
import { HardDrive, RefreshCw, Trash2, WifiOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Illustration } from '@/features/appearance/illustration';
import { ConfirmDialog } from '@/components/forms/confirm-dialog';
import { LiveRegion, announce } from '@/components/feedback/live-region';
import { toast } from '@/components/feedback/toaster';
import { PageHeader } from '@/components/layout/page-header';
import { RecipeCard } from '@/components/recipes/recipe-card';
import { toastError } from '@/lib/errors';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { offlineDb, type SyncConflict } from '@/lib/offline/db';
import {
  useImageSrc,
  useOfflineRecipes,
  usePendingActionCount,
  useSyncConflicts,
} from '@/lib/offline/hooks';
import { offlineManager, type OfflineUsage } from '@/lib/offline/manager';
import {
  discardConflict,
  replayPendingActions,
  resolveConflictKeepBoth,
  startSyncManager,
  type CookingNotePayload,
} from '@/lib/offline/queue';
import { currentFormatLocale } from '@/lib/i18n';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function OfflineRecipeItem({
  entry,
  online,
}: {
  entry: NonNullable<ReturnType<typeof useOfflineRecipes>>[number];
  online: boolean;
}) {
  const { t } = useTranslation();
  const recipe = entry.recipe;
  const thumb = useImageSrc(
    recipe.cover?.thumbnailUrl ?? recipe.cover?.url,
    recipe.cover?.assetId,
    'thumb',
    true,
  );
  const [busy, setBusy] = useState<'refresh' | 'remove' | null>(null);
  const summary = {
    id: recipe.id,
    title: recipe.title,
    description: recipe.description,
    coverThumbnailUrl: thumb,
    totalTimeMinutes: recipe.totalTimeMinutes,
    difficulty: recipe.difficulty,
    servings: recipe.servings,
    rating: recipe.rating,
    isFavorite: recipe.isFavorite,
    origin: recipe.origin,
    status: recipe.status,
    visibility: recipe.visibility,
    tags: recipe.tags,
    categories: recipe.categories,
    cuisine: recipe.cuisine,
    mealType: recipe.mealType,
    sourceDomain: recipe.source?.domain ?? null,
    lastCookedAt: recipe.lastCookedAt,
    updatedAt: recipe.updatedAt,
    createdAt: recipe.createdAt,
  };
  return (
    <RecipeCard
      recipe={summary}
      thumbnailSrc={thumb}
      offlineCopy
      layout="list"
      actions={
        <>
          <span className="self-center text-xs text-muted-foreground">
            {t('offline.savedAt', {
              date: new Date(entry.savedAt).toLocaleDateString(currentFormatLocale()),
            })}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={!online}
            loading={busy === 'refresh'}
            onClick={async () => {
              setBusy('refresh');
              try {
                await offlineManager.refresh(recipe.id);
                toast.success(t('offline.refreshed'));
              } catch (e) {
                toastError(e);
              } finally {
                setBusy(null);
              }
            }}
          >
            <RefreshCw aria-hidden /> {t('offline.refresh')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            loading={busy === 'remove'}
            onClick={async () => {
              setBusy('remove');
              await offlineManager.remove(recipe.id);
              setBusy(null);
            }}
            data-testid="offline-remove"
          >
            <Trash2 aria-hidden /> {t('offline.remove')}
          </Button>
        </>
      }
    />
  );
}

function ConflictItem({ conflict }: { conflict: SyncConflict }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const payload = conflict.localPayload as Partial<CookingNotePayload>;
  return (
    <li className="space-y-2 rounded-lg border border-warning p-3" data-testid="sync-conflict">
      <p className="font-medium">
        {conflict.serverRecipe?.title ?? conflict.recipeId}{' '}
        <Badge variant="warning">{t('offline.conflict.badge')}</Badge>
      </p>
      <div className="grid gap-2 text-sm sm:grid-cols-2">
        <div className="rounded-md bg-muted/50 p-2">
          <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
            {t('offline.conflict.local')}
          </p>
          <p className="whitespace-pre-wrap">
            {payload.note ?? JSON.stringify(conflict.localPayload)}
          </p>
        </div>
        <div className="rounded-md bg-muted/50 p-2">
          <p className="mb-1 text-xs font-medium uppercase text-muted-foreground">
            {t('offline.conflict.server', { version: conflict.serverRecipe?.version ?? '?' })}
          </p>
          <p className="whitespace-pre-wrap">
            {conflict.serverRecipe?.notes ?? t('offline.conflict.noServerNotes')}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          loading={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await resolveConflictKeepBoth(conflict);
              toast.success(t('offline.conflict.merged'));
            } catch (e) {
              toastError(e);
            } finally {
              setBusy(false);
            }
          }}
          data-testid="conflict-keep-both"
        >
          {t('offline.conflict.keepBoth')}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => void discardConflict(conflict)}>
          {t('offline.conflict.discard')}
        </Button>
        {conflict.serverRecipe && (
          <Button asChild size="sm" variant="ghost">
            <Link to={`/recipes/${conflict.recipeId}`}>{t('offline.conflict.openRecipe')}</Link>
          </Button>
        )}
      </div>
    </li>
  );
}

export function OfflinePage() {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const recipes = useOfflineRecipes();
  const pending = usePendingActionCount();
  const conflicts = useSyncConflicts();
  const pendingActions = useLiveQuery(
    () => offlineDb.pendingActions.orderBy('createdAt').toArray(),
    [],
    [],
  );
  const [usage, setUsage] = useState<OfflineUsage | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);

  useEffect(() => startSyncManager(), []);
  useEffect(() => {
    let cancelled = false;
    const load = () => void offlineManager.usage().then((u) => !cancelled && setUsage(u));
    load();
    const unsubscribe = offlineManager.subscribe(load);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const result = await replayPendingActions();
      const message = t('offline.syncResult', {
        done: result.done,
        failed: result.failed,
        conflicts: result.conflicts,
      });
      announce(message);
      toast.show({
        title: message,
        variant: result.failed || result.conflicts ? 'default' : 'success',
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 md:p-6" data-testid="offline-page">
      <LiveRegion />
      <PageHeader title={t('offline.title')} description={t('offline.description')} />

      {!online && (
        <Alert variant="warning">
          <span className="inline-flex items-center gap-2">
            <WifiOff className="size-4" aria-hidden /> {t('app.offline')}
          </span>
        </Alert>
      )}

      <section aria-labelledby="usage-heading" className="grid gap-3 sm:grid-cols-3">
        <h2 id="usage-heading" className="sr-only">
          {t('offline.storage')}
        </h2>
        <div className="rounded-lg border p-3">
          <p className="text-xs text-muted-foreground">{t('offline.recipesStored')}</p>
          <p className="text-2xl font-semibold" data-testid="offline-count">
            {usage?.recipeCount ?? recipes?.length ?? 0}
          </p>
        </div>
        <div className="rounded-lg border p-3">
          <p className="text-xs text-muted-foreground">{t('offline.imagesStored')}</p>
          <p className="text-2xl font-semibold">
            {usage ? `${usage.imageCount} · ${formatBytes(usage.imageBytes)}` : '–'}
          </p>
        </div>
        <div className="rounded-lg border p-3">
          <p className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <HardDrive className="size-3.5" aria-hidden /> {t('offline.deviceStorage')}
          </p>
          <p className="text-2xl font-semibold">
            {usage?.usedBytes !== null && usage?.usedBytes !== undefined && usage.quotaBytes
              ? `${formatBytes(usage.usedBytes)} / ${formatBytes(usage.quotaBytes)}`
              : '–'}
          </p>
        </div>
      </section>

      <section aria-labelledby="sync-heading" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id="sync-heading" className="text-lg font-semibold">
            {t('offline.pendingTitle')}
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void sync()}
            loading={syncing}
            disabled={!online || pending === 0}
            data-testid="sync-now"
          >
            <RefreshCw aria-hidden /> {t('offline.syncNow')}
          </Button>
        </div>
        {pending === 0 ? (
          <p className="text-sm text-muted-foreground">{t('offline.nothingPending')}</p>
        ) : (
          <ul className="divide-y rounded-lg border text-sm" data-testid="pending-list">
            {pendingActions?.map((a) => (
              <li key={a.id} className="flex items-center gap-3 p-2">
                <Badge variant="muted">
                  {t(`offline.kind.${a.kind}`, { defaultValue: a.kind })}
                </Badge>
                <span className="text-muted-foreground">
                  {new Date(a.createdAt).toLocaleString(currentFormatLocale())}
                </span>
                {a.lastError && <span className="ml-auto text-destructive">{a.lastError}</span>}
              </li>
            ))}
          </ul>
        )}
        {conflicts.length > 0 && (
          <ul className="space-y-2" aria-label={t('offline.conflict.title')}>
            {conflicts.map((c) => (
              <ConflictItem key={c.id} conflict={c} />
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="recipes-heading" className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 id="recipes-heading" className="text-lg font-semibold">
            {t('offline.downloadedRecipes')}
          </h2>
          {recipes && recipes.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setClearOpen(true)}>
              <Trash2 aria-hidden /> {t('offline.removeAll')}
            </Button>
          )}
        </div>
        {recipes === undefined ? (
          <Skeleton className="h-32" />
        ) : recipes.length === 0 ? (
          <EmptyState
            illustration={<Illustration name="offline" />}
            title={t('offline.empty')}
            description={t('offline.emptyHint')}
            action={
              <Button asChild>
                <Link to="/recipes">{t('recipes.library.title')}</Link>
              </Button>
            }
          />
        ) : (
          <ul className="space-y-3" data-testid="offline-list">
            {recipes.map((entry) => (
              <li key={entry.id}>
                <OfflineRecipeItem entry={entry} online={online} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <ConfirmDialog
        open={clearOpen}
        onOpenChange={setClearOpen}
        title={t('offline.removeAllTitle')}
        description={t('offline.removeAllDescription')}
        confirmLabel={t('offline.removeAll')}
        destructive
        onConfirm={async () => {
          await offlineManager.removeAll();
          setClearOpen(false);
        }}
      />
    </div>
  );
}
